import { FC } from 'react';
import { Alert, Badge, Card, Col, ListGroup, Row } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import CaresPageHeader from '../shell/cares-page-header';
import ClientsPage from './clients/clients-page';
import { getCaresShellBootstrap } from '../shell/bootstrap';
import AdminAuditActivityPage from './audit-activity/audit-activity-page';
import AdminSystemSettingsPage from './system-settings/theme-options/theme-options-page';
import AdminUsersAccessPage from './users-access/users-access-page';
import AdminOverviewPage from './admin-overview-page';
import {
  adminSections,
  type AdminSection,
} from './config';

type AdminPageProps = {
  sectionId: string;
};

const toneClassMap: Record<AdminSection['summaryCards'][number]['tone'], string> = {
  primary: 'primary',
  success: 'success',
  warning: 'warning',
  info: 'info',
};

const AdminPage: FC<AdminPageProps> = ({ sectionId }) => {
  const bootstrap = getCaresShellBootstrap();
  const currentSection =
    adminSections.find((section) => section.id === sectionId) ??
    adminSections[0];

  if (!bootstrap.authenticated || !bootstrap.canAccessAdmin) {
    return (
      <>
        <CaresPageHeader homepage="CARES Administration" activepage="Admin" page="Access Required" />
        <Alert variant="danger" className="custom-card">
          This area is limited to authenticated platform administrators.
          {!bootstrap.authenticated ? (
            <>
              {' '}
              <Link to="/login" className="alert-link">
                Sign in
              </Link>{' '}
              to continue.
            </>
          ) : null}
        </Alert>
      </>
    );
  }

  if (currentSection.id === 'clients') {
    return <ClientsPage />;
  }

  if (currentSection.id === 'overview') {
    return <AdminOverviewPage />;
  }

  if (currentSection.id === 'users-access') {
    return <AdminUsersAccessPage section={currentSection} ownerEmail={bootstrap.user?.email} />;
  }

  if (currentSection.id === 'theme-options') {
    return <AdminSystemSettingsPage section={currentSection} ownerEmail={bootstrap.user?.email} />;
  }

  if (currentSection.id === 'audit-activity') {
    return <AdminAuditActivityPage section={currentSection} ownerEmail={bootstrap.user?.email} />;
  }

  return (
    <>
      <CaresPageHeader homepage="CARES Administration" activepage="Admin" page={currentSection.navLabel} actions={<></>} />

      <Row className="g-4">
        <Col xl={12}>
          <Card className="custom-card cares-page-hero">
            <Card.Body className="p-4 p-xxl-5">
              <div className="d-flex flex-wrap justify-content-between align-items-start gap-3 mb-4">
                <div>
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <Badge
                      bg=""
                      className={`bg-${toneClassMap[currentSection.statusTone]}-transparent text-${toneClassMap[currentSection.statusTone]}`}
                    >
                      {currentSection.statusLabel}
                    </Badge>
                    <span className="fs-12 text-muted">
                      Platform owner: {bootstrap.user?.email}
                    </span>
                  </div>
                  <h2 className="mb-2">{currentSection.title}</h2>
                  <p className="text-muted mb-0">
                    {currentSection.description}
                  </p>
                </div>
                <div className="text-start text-xl-end">
                  <p className="fs-12 text-uppercase text-muted mb-2">Page type</p>
                  <h3 className="mb-2">Operator state</h3>
                  <p className="text-muted mb-0 fs-13">
                    Available actions and unavailable capabilities are listed explicitly below.
                  </p>
                </div>
              </div>

              <Row className="g-3 mb-4">
                {currentSection.summaryCards.map((card) => (
                  <Col md={4} key={card.label}>
                    <Card className="custom-card border h-100 shadow-none mb-0">
                      <Card.Body>
                        <span className={`badge bg-${toneClassMap[card.tone]}-transparent text-${toneClassMap[card.tone]} mb-3`}>
                          {card.label}
                        </span>
                        <h4 className="mb-0">{card.value}</h4>
                      </Card.Body>
                    </Card>
                  </Col>
                ))}
              </Row>

              <Row className="g-4">
                <Col xxl={7}>
                  <Card className="custom-card border shadow-none h-100 mb-0">
                    <Card.Header>
                      <div className="card-title">Available Now</div>
                    </Card.Header>
                    <Card.Body>
                      <ListGroup variant="flush">
                        {currentSection.availableNow.map((item) => (
                          <ListGroup.Item key={item} className="px-0">
                            <div className="d-flex align-items-start gap-2">
                              <span className="badge bg-success-transparent text-success mt-1">
                                <i className="ri-check-line"></i>
                              </span>
                              <span className="text-muted">{item}</span>
                            </div>
                          </ListGroup.Item>
                        ))}
                      </ListGroup>
                    </Card.Body>
                  </Card>
                </Col>
                <Col xxl={5}>
                  <Card className="custom-card border shadow-none h-100 mb-0">
                    <Card.Header>
                      <div className="card-title">Where To Work</div>
                    </Card.Header>
                    <Card.Body>
                      <ListGroup variant="flush">
                        {currentSection.actionLinks.map((item) => (
                          <ListGroup.Item key={item.label} className="px-0">
                            <div className="d-flex justify-content-between align-items-start gap-3">
                              <div>
                                <div className="fw-medium">
                                  {item.path ? (
                                    <Link to={item.path}>{item.label}</Link>
                                  ) : (
                                    item.label
                                  )}
                                </div>
                                <div className="fs-12 text-muted">
                                  {item.path ? item.path : 'No destination route is live in CARES today.'}
                                </div>
                              </div>
                              <Badge
                                bg=""
                                className={`bg-${toneClassMap[item.tone]}-transparent text-${toneClassMap[item.tone]}`}
                              >
                                {item.status}
                              </Badge>
                            </div>
                          </ListGroup.Item>
                        ))}
                      </ListGroup>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              <Card className="custom-card border shadow-none mb-0 mt-4">
                <Card.Header>
                  <div className="card-title">Unavailable On This Page</div>
                </Card.Header>
                <Card.Body>
                  <ListGroup variant="flush">
                    {currentSection.unavailableHere.map((item) => (
                      <ListGroup.Item key={item} className="px-0">
                        <div className="d-flex align-items-start gap-2">
                          <span className="badge bg-warning-transparent text-warning mt-1">
                            <i className="ri-forbid-2-line"></i>
                          </span>
                          <span className="text-muted">{item}</span>
                        </div>
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                </Card.Body>
              </Card>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </>
  );
};

export default AdminPage;
